/**
 * Seating consistency when guests are linked as companions or gain an anonymous
 * plus-one slot. Runs at guest write time (not on the seating canvas reconcile loop).
 */

import { deleteField, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import type { Guest, PlusOne, Table } from "@amazing-wedding/shared-types";
import { guestSeatingFieldPath } from "@amazing-wedding/shared-types";
import { guestRef, planTableRef } from "@/features/seating/firestore-paths";
import { unassignGuestFromAllPlans, unassignGuestWithSeatMap } from "@/features/seating/operations";
import { fetchAllPlansWithTables } from "@/features/seating/plan-operations";
import {
  buildSeatMapFromGuestIds,
  guestOccupantKeys,
  occupantKey,
} from "@/features/seating/seat-map";
import type { GuestPatch } from "./operations";

export function isAnonymousPlusOneSlot(plusOne: PlusOne | null | undefined): boolean {
  if (plusOne == null) return false;
  return !plusOne.firstName?.trim() && !plusOne.guestId?.trim();
}

/** True when the patch introduces or replaces with an anonymous plus-one slot. */
export function plusOneBecameAnonymous(before: Guest, patch: GuestPatch): boolean {
  if (patch.plusOne === undefined) return false;
  const after = patch.plusOne;
  if (!isAnonymousPlusOneSlot(after)) return false;
  return !isAnonymousPlusOneSlot(before.plusOne);
}

function seatingKeysPresent(seatMap: (string | null)[], keys: string[]): boolean {
  const present = new Set(seatMap.filter((k): k is string => k !== null));
  return keys.every((k) => present.has(k));
}

/** Persisted seat map only — avoids reconcile expanding incomplete parties in memory. */
function persistedSeatMap(table: Table, guestsById: Record<string, Guest>): (string | null)[] {
  if (table.seatMap.length > 0) {
    const map = [...table.seatMap];
    while (map.length < table.capacity) map.push(null);
    return map.slice(0, table.capacity);
  }
  return buildSeatMapFromGuestIds(table, guestsById);
}

function areSeatIndexesAdjacent(a: number, b: number, capacity: number): boolean {
  if (capacity <= 1) return false;
  const diff = Math.abs(a - b);
  return diff === 1 || diff === capacity - 1;
}

function seatIndexForHostKey(
  seatMap: (string | null)[],
  hostKey: string,
  fallback?: number | null,
): number {
  const inMap = seatMap.findIndex((k) => k === hostKey);
  if (inMap >= 0) return inMap;
  if (typeof fallback === "number" && fallback >= 0 && fallback < seatMap.length) return fallback;
  return -1;
}

function ensureGuestIdPresent(guestIds: string[], guestId: string): string[] {
  if (guestIds.includes(guestId)) return guestIds;
  return [...guestIds, guestId];
}

function ensureGuestIdAfterHost(guestIds: string[], hostId: string, guestId: string): string[] {
  if (guestIds.includes(guestId)) return guestIds;
  const next = [...guestIds];
  const hostIndex = next.indexOf(hostId);
  if (hostIndex < 0) {
    next.push(guestId);
    return next;
  }
  next.splice(hostIndex + 1, 0, guestId);
  return next;
}

function standaloneCompanionSeatIndexes(params: {
  seatMap: (string | null)[];
  host: Guest;
  companion: Guest;
  planId: string;
}): { hostSeat: number; companionSeat: number } | null {
  const { seatMap, host, companion, planId } = params;
  const hostSeat = seatIndexForHostKey(seatMap, host.id, host.seatingByPlan?.[planId]?.seatNumber);
  const companionSeat = seatIndexForHostKey(
    seatMap,
    companion.id,
    companion.seatingByPlan?.[planId]?.seatNumber,
  );
  if (hostSeat < 0 || companionSeat < 0) return null;
  return { hostSeat, companionSeat };
}

async function tryConvertStandaloneAdjacentCompanionsToLinked(params: {
  weddingId: string;
  planId: string;
  host: Guest;
  companion: Guest;
  table: Table;
  guestsById: Record<string, Guest>;
}): Promise<boolean> {
  const { weddingId, planId, host, companion, table, guestsById } = params;

  const hostSeating = host.seatingByPlan?.[planId];
  const companionSeating = companion.seatingByPlan?.[planId];
  if (!hostSeating?.tableId || !companionSeating?.tableId) return false;
  if (hostSeating.tableId !== table.id || companionSeating.tableId !== table.id) return false;

  const seatMap = persistedSeatMap(table, guestsById);
  const seats = standaloneCompanionSeatIndexes({ seatMap, host, companion, planId });
  if (!seats) return false;
  if (!areSeatIndexesAdjacent(seats.hostSeat, seats.companionSeat, seatMap.length)) return false;

  const linkedPlusOneKey = occupantKey({ guestId: host.id, type: "plusOne" });
  const nextSeatMap = [...seatMap];
  nextSeatMap[seats.companionSeat] = linkedPlusOneKey;

  await updateDoc(planTableRef(weddingId, planId, table.id), {
    seatMap: nextSeatMap,
    guestIds: ensureGuestIdPresent(
      table.guestIds.filter((id) => id !== companion.id),
      host.id,
    ),
    updatedAt: serverTimestamp(),
  });
  await updateDoc(guestRef(weddingId, companion.id), {
    [guestSeatingFieldPath(planId)]: deleteField(),
    updatedAt: serverTimestamp(),
  });
  return true;
}

async function tryConvertLinkedAdjacentCompanionsToStandalone(params: {
  weddingId: string;
  planId: string;
  host: Guest;
  companion: Guest;
  table: Table;
  guestsById: Record<string, Guest>;
}): Promise<boolean> {
  const { weddingId, planId, host, companion, table, guestsById } = params;

  const hostSeating = host.seatingByPlan?.[planId];
  if (!hostSeating?.tableId || hostSeating.tableId !== table.id) return false;

  const seatMap = persistedSeatMap(table, guestsById);
  const hostSeat = seatIndexForHostKey(seatMap, host.id, hostSeating.seatNumber);
  const linkedPlusOneKey = occupantKey({ guestId: host.id, type: "plusOne" });
  const linkedSeat = seatMap.findIndex((k) => k === linkedPlusOneKey);
  if (hostSeat < 0 || linkedSeat < 0) return false;
  if (!areSeatIndexesAdjacent(hostSeat, linkedSeat, seatMap.length)) return false;

  const nextSeatMap = [...seatMap];
  nextSeatMap[linkedSeat] = companion.id;

  await updateDoc(planTableRef(weddingId, planId, table.id), {
    seatMap: nextSeatMap,
    guestIds: ensureGuestIdAfterHost(
      ensureGuestIdPresent(table.guestIds, host.id),
      host.id,
      companion.id,
    ),
    updatedAt: serverTimestamp(),
  });
  await updateDoc(guestRef(weddingId, companion.id), {
    [guestSeatingFieldPath(planId)]: { tableId: table.id, seatNumber: linkedSeat },
    updatedAt: serverTimestamp(),
  });
  return true;
}

export function areLinkedCompanionsCoSeatedInPlan(
  host: Guest,
  companionGuestId: string,
  planId: string,
  tables: Table[],
  guestsById: Record<string, Guest>,
): boolean {
  const companion = guestsById[companionGuestId];
  const hostSeating = host.seatingByPlan?.[planId];
  const companionSeating = companion?.seatingByPlan?.[planId];

  if (companionSeating?.tableId) return false;

  if (!hostSeating?.tableId) return true;

  const table = tables.find((t) => t.id === hostSeating.tableId);
  if (!table) return false;

  const linkedHost: Guest = {
    ...host,
    plusOne: {
      firstName: companion?.firstName ?? host.plusOne?.firstName ?? "",
      lastName: companion?.lastName ?? host.plusOne?.lastName ?? null,
      mealPreference: host.plusOne?.mealPreference ?? null,
      guestId: companionGuestId,
    },
    plusOneAllowed: true,
  };

  const expectedKeys = guestOccupantKeys(linkedHost);
  const seatMap = persistedSeatMap(table, guestsById);
  return seatingKeysPresent(seatMap, expectedKeys);
}

async function fetchGuestDoc(weddingId: string, guestId: string): Promise<Guest | null> {
  const snap = await getDoc(guestRef(weddingId, guestId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Guest;
}

async function unassignGuestInPlan(
  weddingId: string,
  planId: string,
  guest: Guest,
  tables: Table[],
  guestsById: Record<string, Guest>,
): Promise<void> {
  const seating = guest.seatingByPlan?.[planId];
  if (!seating?.tableId) return;

  const table = tables.find((t) => t.id === seating.tableId);
  if (!table) {
    await updateDoc(guestRef(weddingId, guest.id), {
      [guestSeatingFieldPath(planId)]: deleteField(),
      updatedAt: serverTimestamp(),
    });
    return;
  }

  await unassignGuestWithSeatMap(
    weddingId,
    planId,
    guest.id,
    seating.tableId,
    table,
    guestsById,
    tables.map((t) => t.id),
  );
}

export async function enforceLinkedCompanionSeating(
  weddingId: string,
  hostGuestId: string,
  companionGuestId: string,
): Promise<void> {
  const plansTables = await fetchAllPlansWithTables(weddingId);
  if (plansTables.length === 0) return;

  for (const { planId, tables } of plansTables) {
    let host = await fetchGuestDoc(weddingId, hostGuestId);
    if (!host) return;

    let companion = await fetchGuestDoc(weddingId, companionGuestId);
    const guestsById: Record<string, Guest> = { [hostGuestId]: host };
    if (companion) guestsById[companionGuestId] = companion;

    const hostTableId = host.seatingByPlan?.[planId]?.tableId;
    if (hostTableId && companion) {
      const hostTable = tables.find((t) => t.id === hostTableId);
      if (
        hostTable &&
        (await tryConvertStandaloneAdjacentCompanionsToLinked({
          weddingId,
          planId,
          host,
          companion,
          table: hostTable,
          guestsById,
        }))
      ) {
        continue;
      }
    }

    if (areLinkedCompanionsCoSeatedInPlan(host, companionGuestId, planId, tables, guestsById)) {
      continue;
    }

    if (companion) {
      await unassignGuestInPlan(weddingId, planId, companion, tables, guestsById);
      companion = await fetchGuestDoc(weddingId, companionGuestId);
      if (companion) guestsById[companionGuestId] = companion;
    }

    host = await fetchGuestDoc(weddingId, hostGuestId);
    if (!host) continue;
    guestsById[hostGuestId] = host;

    await unassignGuestInPlan(weddingId, planId, host, tables, guestsById);
  }
}

export async function enforceUnlinkedCompanionSeating(
  weddingId: string,
  hostGuestId: string,
  companionGuestId: string,
): Promise<void> {
  const plansTables = await fetchAllPlansWithTables(weddingId);
  if (plansTables.length === 0) return;

  for (const { planId, tables } of plansTables) {
    let host = await fetchGuestDoc(weddingId, hostGuestId);
    if (!host) return;

    let companion = await fetchGuestDoc(weddingId, companionGuestId);
    if (!companion) return;

    const guestsById: Record<string, Guest> = {
      [hostGuestId]: host,
      [companionGuestId]: companion,
    };

    const hostTableId = host.seatingByPlan?.[planId]?.tableId;
    if (hostTableId) {
      const hostTable = tables.find((t) => t.id === hostTableId);
      if (
        hostTable &&
        (await tryConvertLinkedAdjacentCompanionsToStandalone({
          weddingId,
          planId,
          host,
          companion,
          table: hostTable,
          guestsById,
        }))
      ) {
        continue;
      }
    }

    const hostSeating = host.seatingByPlan?.[planId];
    const companionSeating = companion.seatingByPlan?.[planId];
    if (
      hostSeating?.tableId &&
      companionSeating?.tableId &&
      hostSeating.tableId === companionSeating.tableId
    ) {
      const sameTable = tables.find((t) => t.id === hostSeating.tableId);
      if (sameTable) {
        const seatMap = persistedSeatMap(sameTable, guestsById);
        const seats = standaloneCompanionSeatIndexes({ seatMap, host, companion, planId });
        if (seats && areSeatIndexesAdjacent(seats.hostSeat, seats.companionSeat, seatMap.length)) {
          continue;
        }
      }
    }

    await unassignGuestInPlan(weddingId, planId, companion, tables, guestsById);
    companion = await fetchGuestDoc(weddingId, companionGuestId);
    if (companion) guestsById[companionGuestId] = companion;

    host = await fetchGuestDoc(weddingId, hostGuestId);
    if (!host) continue;
    guestsById[hostGuestId] = host;

    await unassignGuestInPlan(weddingId, planId, host, tables, guestsById);
  }
}

export async function enforceAnonymousPlusOneSeating(
  weddingId: string,
  hostGuestId: string,
): Promise<void> {
  const plansTables = await fetchAllPlansWithTables(weddingId);
  if (plansTables.length === 0) return;

  const host = await fetchGuestDoc(weddingId, hostGuestId);
  if (!host) return;

  await unassignGuestFromAllPlans(weddingId, host, plansTables, { [hostGuestId]: host });
}
